"use client";

import React, { useCallback, useEffect, useState } from "react";
import Image from "next/image";
import { GroqImage } from "@/helpers/Image";

import { PortableText } from "@portabletext/react";
import { customRichTextComponent } from "@/sanity/sanityPortableText";
import { CTAButton } from "@/shadcn/ui/button";

import Autoplay from "embla-carousel-autoplay";
import {
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselItem,
} from "@/shadcn/ui/carousel";

import { PageLaunchSchema } from "@/sanity/pages/PageLaunchTemplate";

export default function About({ data }: { data: PageLaunchSchema }) {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);

  const changeCurrent = useCallback(
    (index: number) => {
      if (!api) {
        return;
      }

      api.scrollTo(index);
    },
    [api]
  );

  useEffect(() => {
    if (!api) {
      return;
    }

    setCurrent(api.selectedScrollSnap());

    const onSelect = () => {
      setCurrent(api.selectedScrollSnap());
    };

    api.on("select", onSelect);

    return () => {
      api.off("select", onSelect);
    };
  }, [api]);

  return (
    <section className="relative pb-[2rem] lg:pb-[4rem]">
      <div className="px-[2rem] w-[min(152.5rem,100%)] mx-auto grid grid-cols-1 lg:grid-cols-2">
        <div className="flex flex-col items-center lg:items-start order-2 lg:order-1">
          <div className="w-[min(65rem,100%)] flex flex-col items-center lg:items-start lg:pe-[8rem]">
            {data?.carouselTexts1 && (
              <PortableText
                value={data?.carouselTexts1 ?? []}
                components={customRichTextComponent({
                  block: {
                    h2: ({ children }) => (
                      <h2 className="hidden lg:block text-[5rem] lg:text-[7rem] font-semibold !text-white leading-[1.2em] mb-[2rem] tracking-[-.4rem] text-center lg:text-start">
                        {children}
                      </h2>
                    ),
                    normal: ({ children }) => (
                      <p className="text-white text-opacity-60 text-[2rem] w-[min(57rem,100%)] mb-[3rem] text-center lg:text-start">
                        {children}
                      </p>
                    ),
                  },
                })}
              />
            )}

            <div className="w-full border-t [border-image:repeating-linear-gradient(to_right,white_0,white_5px,transparent_5px,transparent_10px)_1] opacity-20 mb-[3rem]"></div>

            {data?.carouselTexts2 && (
              <PortableText
                value={data?.carouselTexts2 ?? []}
                components={customRichTextComponent({
                  block: {
                    normal: ({ children }) => (
                      <p className="text-white text-opacity-60 text-[2rem] w-[min(57rem,100%)] mb-[3rem] text-center lg:text-start px-[2rem] lg:px-0">
                        {children}
                      </p>
                    ),
                  },
                  marks: {
                    strong: ({ children }) => (
                      <span className="text-white block mb-[-4rem]">
                        {children}
                      </span>
                    ),
                  },
                })}
              />
            )}

            <div className="w-full lg:w-fit">
              {data?.carouselCTA && <CTAButton data={data?.carouselCTA} />}
            </div>
          </div>
        </div>

        <div className="flex flex-col items-center justify-center order-1 lg:order-2 mb-[4rem] lg:mb-0">
          <h2 className="lg:hidden text-[5rem] lg:text-[7rem] font-semibold !text-white leading-[1.2em] mb-[2rem] tracking-[-.4rem] text-center lg:text-start">
            Über Bitbull <br />& Trademania
          </h2>

          <Carousel
            setApi={setApi}
            opts={{
              align: "center",
              loop: true,
            }}
            plugins={[
              Autoplay({
                delay: 5000,
                stopOnInteraction: false,
                stopOnMouseEnter: true,
              }),
            ]}
            className="w-full max-w-[72rem]"
          >
            <CarouselContent className="!-ml-2 lg:-ml-4">
              {data?.carouselPhotos?.map((photo, index) => (
                <CarouselItem key={index} className="!pl-2 lg:!pl-4">
                  <div className="w-full max-w-[50rem] lg:max-w-[70rem] lg:p-1 relative">
                    <GroqImage
                      src={photo}
                      alt="photo"
                      className="!w-full !h-full"
                      quality={100}
                    />
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>
          {/* constrols */}
          <div className="w-full max-w-[70rem] flex flex-col items-center justify-center gap-[8rem] mt-[1rem] lg:mt-[2rem]">
            <div className="w-full flex items-center justify-center gap-[.25rem]">
              <div className="w-full flex items-stretch justify-center gap-[0.5rem]">
                {data?.carouselPhotos?.map((photo, index) => {
                  return (
                    <button
                      key={index}
                      onClick={() => changeCurrent(index)}
                      className={`${
                        index === current
                          ? "lg:!w-[8.5rem] border-[#61FFCE] opacity-100"
                          : "border-[#757575] opacity-50"
                      } !w-[6.9rem] border-[.25rem] rounded-[.5rem] transition-all`}
                    >
                      <Image
                        src={photo}
                        alt="photo"
                        className="!w-full !h-[5rem] lg:!h-[6rem] object-cover rounded-[.5rem] overflow-clip"
                        quality={100}
                      />
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
          {/* end constrols */}
        </div>
      </div>
    </section>
  );
}
